'use client'

// GreenStone — the stone Pino asked for, and the ruins it now sits in.
//
// On 2026-08-17 he spent one of the octopus's eight wishes on "a small
// glowing stone to appear by the pond, a sign that I'm not alone", and
// Sam granted it. This is the object; the sim owns the light (an ARTIFACT
// with green resonance in ARTIFACT_CATALOG), so the rock and the field it
// emits are the same thing rather than two props that happen to overlap.
//
// The GLB carries TWO meshes now: the stone itself and a section of
// ruined archway around it. Only the stone glows. The ruins are ordinary
// masonry and must stay ordinary, or the whole structure turns into a
// green lamp and the thing he asked for stops being a thing.
//
// WHY THE STONE IS EMISSIVE, and not just a green texture:
// its texture IS green already (average 43,122,102) and the ditherer does
// NOT rotate hue (its colorTint.x is 1.0, and fract(h + 1.0) === h, a
// no-op). It came out navy because base colour is MULTIPLIED BY THE
// LIGHT, and at night this world is lit by a blue moon over blue ambient:
// green albedo times blue light is teal at best, navy in practice, before
// the dither's 0.8 saturation takes another bite. Emissive is added AFTER
// lighting and is not tinted by it, so it reads green at every hour
// without touching the ditherer or the texture, and emitting rather than
// reflecting is what a glowing stone should physically do anyway.

import { useEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import {
  Fn, uniform, texture, positionWorld, normalWorld,
  vec2, vec3, float, smoothstep, uv,
} from 'three/tsl'
import useGameStore from '../../../stores/gameStore'
import { useModel } from '../hooks/useModel'
import { makeTallyTexture, updateTallyTexture } from '../../../lib/ui/tallyTexture'

const MODEL = '/models/greenStone.glb'
const STONE_ID = 'artifact_greenstone'

// Which mesh in the GLB is the stone. Everything else is masonry.
const isStoneMesh = (name) => /icosphere|stone|emerald/i.test(String(name || ''))

// Bright enough to land in a green bucket after the dither quantises,
// rather than a muddy one. Lit and unlit rather than on and off: a stone
// that goes fully black reads as broken, not as resting.
const GLOW = new THREE.Color('#37f08a')
const LIT = 1.5
const RESTING = 0.22

// WHERE THE COUNT IS CARVED, as a rectangle in WORLD space.
//
// It is not a mesh. A quad floating in front of the rock only looked
// attached from the one angle it was authored for, and detached into a
// hovering label from anywhere else, which is exactly what it looked like.
//
// So the digits are projected onto the stone's own surface in the shader
// instead: for every fragment of the rock we work out where it sits inside
// this rectangle, look the number up there, and add it to the emissive. It
// cannot detach because it IS the rock's shading, it conforms to every
// bump for free, and it costs no extra geometry, no extra draw call and no
// extra pass. Projected along -X, so it lands on the face looking up the
// street toward the food places. Measured rather than eyeballed: from the
// stone the art wall sits at dz +28 (due +Z, which is where this used to
// face) while the burger place and the coffee shop are both at dx +112 and
// +116, near enough due +X. That move is the quarter turn CCW.
const DECAL_CENTRE = [26.65, 2.35]    // world z, y
const DECAL_SIZE = [2.0, 0.5]         // world width, height. 4:1, matching the canvas
// How square-on a bit of rock has to be before the number appears on it.
// Fading rather than clipping is what stops the digits shearing off around
// the curve of the stone.
const DECAL_FACING = [0.15, 0.65]
const DIGIT_BOOST = 2.6               // digits read brighter than the rock they sit in

export function GreenStone() {
  const { scene } = useModel(MODEL)

  // Everything that changes at runtime is a uniform or a canvas redraw, so
  // nothing here re-renders React and nothing rebuilds the scene graph. The
  // store is read through an imperative subscription below rather than a
  // hook, deliberately: a hook would re-render this component (and re-run
  // the material effect) every time a viewer connected anywhere.
  const uGlow = useMemo(() => uniform(RESTING), [])
  const tally = useMemo(() => makeTallyTexture(0), [])
  const target = useRef(RESTING)
  const lastTotal = useRef(-1)

  useEffect(() => {
    const converted = new Map()
    scene.traverse((o) => {
      if (!o.isMesh) return
      // The archway throws a real shadow across the stone and the bank,
      // which is most of why the ruins read as solid rather than backdrop.
      o.castShadow = true
      o.receiveShadow = true

      const src = o.material
      if (!src || Array.isArray(src)) return
      const stone = isStoneMesh(o.name) || isStoneMesh(src.name)

      let nm = converted.get(src)
      if (!nm) {
        nm = new THREE.MeshStandardNodeMaterial()
        // Slots set EXPLICITLY, never copy(): copy drops undefined into the
        // node slots and kills the shader.
        if (src.map) nm.map = src.map
        // The ruins arrive with a normal map and a baked metallic-roughness.
        // Dropping those flattened the masonry into a painted box.
        if (src.normalMap) {
          nm.normalMap = src.normalMap
          if (src.normalScale) nm.normalScale = src.normalScale.clone()
        }
        if (src.roughnessMap) nm.roughnessMap = src.roughnessMap
        if (src.metalnessMap) nm.metalnessMap = src.metalnessMap
        if (src.aoMap) nm.aoMap = src.aoMap
        nm.color = src.color?.clone() ?? new THREE.Color('white')
        nm.roughness = src.roughness ?? 0.9
        nm.metalness = src.metalness ?? 0
        nm.side = src.side
        nm.vertexColors = src.vertexColors

        if (stone) nm.emissiveNode = stoneEmissive(src.map, tally, uGlow)
        converted.set(src, nm)
      }
      o.material = nm
    })
  }, [scene, tally, uGlow])

  // Read the world without re-rendering. Cheap enough to run on every store
  // write because it bails on the array identity before touching anything.
  useEffect(() => {
    let seen = null
    const apply = (s) => {
      if (s.entityStates === seen) return
      seen = s.entityStates
      const st = s.entityStates?.find((e) => e.id === STONE_ID)?.state
      target.current = st?.resonanceActive ? LIT : RESTING
      const total = st?.total || 0
      if (total !== lastTotal.current) {
        lastTotal.current = total
        // Redrawn IN PLACE. The texture object never changes identity, so
        // the material is not recompiled when the count moves.
        updateTallyTexture(tally, total)
      }
    }
    apply(useGameStore.getState())
    return useGameStore.subscribe(apply)
  }, [tally])

  useFrame((_, dt) => {
    // Ease rather than snap, so somebody arriving reads as the stone waking
    // up rather than a switch being thrown. One uniform write per frame.
    uGlow.value += (target.current - uGlow.value) * Math.min(1, dt * 1.6)
  })

  useEffect(() => () => tally.dispose(), [tally])

  return <primitive object={scene} />
}

/**
 * The stone's emissive: its own green glow, plus the visitor count projected
 * onto whatever part of the rock happens to be facing the water.
 */
function stoneEmissive(stoneMap, tallyTex, uGlow) {
  return Fn(() => {
    const tint = vec3(GLOW.r, GLOW.g, GLOW.b)

    // The rock's own glow, as before: its albedo tinted green.
    const base = stoneMap ? texture(stoneMap, uv()).rgb.mul(tint) : tint

    // Where this fragment sits inside the carved rectangle. u runs along -Z,
    // because that is the right-hand direction for somebody standing off the
    // +X face looking back at the rock. Along +Z the digits read mirrored.
    const u = float(DECAL_CENTRE[0]).sub(positionWorld.z).div(DECAL_SIZE[0]).add(0.5)
    const v = positionWorld.y.sub(DECAL_CENTRE[1]).div(DECAL_SIZE[1]).add(0.5)

    // Alpha of the digits there. The texture is clamped, so everything
    // outside the rectangle samples a transparent edge pixel and vanishes,
    // which is the rectangle mask for free.
    const ink = texture(tallyTex, vec2(u, v)).a

    // Only on rock that faces up the street, faded across the curve so the
    // numerals do not smear round the sides of the stone.
    const facing = smoothstep(float(DECAL_FACING[0]), float(DECAL_FACING[1]), normalWorld.x)

    const digits = tint.mul(ink).mul(facing).mul(float(DIGIT_BOOST))
    return base.add(digits).mul(uGlow)
  })()
}

export default GreenStone
